import React from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import "./BreadcrumbNav.css";

export interface BreadcrumbItem {
  label: string;
  href?: string;
  isClickable?: boolean;
  isCurrent?: boolean;
  dropdownItems?: { label: string; href: string }[];
  onCurrentClick?: () => void;
}

interface BreadcrumbNavProps {
  items: BreadcrumbItem[];
  onNavigate?: (href: string) => void;
  currentPageUrl?: string;
  actions?: React.ReactNode;
}

export function BreadcrumbNav({
  items = [],
  onNavigate,
  currentPageUrl,
  actions,
}: BreadcrumbNavProps) {
  const navigate = useNavigate();
  const [openDropdown, setOpenDropdown] = React.useState<number | null>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const safeItems = Array.isArray(items) ? items : [];

  const handleClick = (item: BreadcrumbItem, index: number) => {
    if (item.isCurrent && item.onCurrentClick) {
      item.onCurrentClick();
      return;
    }

    if (item.dropdownItems && item.dropdownItems.length > 0) {
      setOpenDropdown(openDropdown === index ? null : index);
      return;
    }

    if (item.isClickable && item.href) {
      if (onNavigate) {
        onNavigate(item.href);
      } else {
        navigate(item.href);
      }
    }
  };

  const handleDropdownNavigate = (href: string) => {
    setOpenDropdown(null);
    if (onNavigate) {
      onNavigate(href);
    } else {
      navigate(href);
    }
  };

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpenDropdown(null);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Calculate dropdown width based on longest text
  const calculateDropdownWidth = (
    dropdownItems: { label: string; href: string }[] | undefined,
  ) => {
    if (!dropdownItems || dropdownItems.length === 0) return "auto";

    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    if (!context) return "auto";

    context.font = "500 0.875rem sans-serif";
    let maxWidth = 0;

    dropdownItems.forEach((item) => {
      const textWidth = context.measureText(item.label).width;
      maxWidth = Math.max(maxWidth, textWidth);
    });

    return `${maxWidth + 32}px`;
  };

  return (
    <nav
      className="breadcrumb-nav"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <ul className="breadcrumb-list">
        {safeItems.map((item, index) => (
          <li
            key={index}
            className={`breadcrumb-item${
              openDropdown === index ? " open" : ""
            }`}
          >
            {item.isCurrent ? (
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleClick(item, index);
                }}
                className="breadcrumb-link"
                title="Click to copy link"
              >
                {item.label}
              </a>
            ) : item.dropdownItems && item.dropdownItems.length > 0 ? (
              <div
                style={{ position: "relative" }}
                ref={index === openDropdown ? dropdownRef : undefined}
              >
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleClick(item, index);
                  }}
                  className={`breadcrumb-link breadcrumb-dropdown-trigger ${
                    openDropdown === index ? "open" : ""
                  }`}
                  aria-expanded={openDropdown === index}
                >
                  {item.label}
                  <ChevronDown className="breadcrumb-chevron" aria-hidden />
                </a>
                {openDropdown === index &&
                  Array.isArray(item.dropdownItems) && (
                    <div className="breadcrumb-dropdown">
                      {item.dropdownItems.map((dropdownItem, dropdownIndex) => (
                        <a
                          key={dropdownIndex}
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            handleDropdownNavigate(dropdownItem.href);
                          }}
                          className="breadcrumb-dropdown-item"
                        >
                          {dropdownItem.label}
                        </a>
                      ))}
                    </div>
                  )}
              </div>
            ) : item.isClickable ? (
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleClick(item, index);
                }}
                className="breadcrumb-link"
              >
                {item.label}
              </a>
            ) : (
              <span
                className="breadcrumb-link"
                style={{ cursor: "default", color: "#999" }}
              >
                {item.label}
              </span>
            )}
          </li>
        ))}
      </ul>
      {actions && (
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          {actions}
        </div>
      )}
    </nav>
  );
}
